<template>
  <div class="relative inline-block">
    <!-- Trigger element (unit chip) -->
    <div ref="triggerRef" @click="togglePopover" @mouseenter="showOnHover && (isOpen = true)"
      @mouseleave="showOnHover && (isOpen = false)" class="cursor-pointer">
      <slot name="trigger" :isOpen="isOpen" />
    </div>

    <!-- Popover content -->
    <Teleport to="body">
      <div v-if="isOpen" ref="popoverRef" class="popover-content" :style="popoverStyle">
        <div class="popover-body">
          <div class="popover-header">
            <h3 class="popover-title">{{ unitName }}</h3>
            <button @click="closePopover" class="popover-close-btn">
              ×
            </button>
          </div>

          <div v-if="incidentData" class="popover-data">
            <div class="data-grid">
              <!-- Status with icon -->
              <div class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">📊</span>
                  Status
                </span>
                <span :class="incidentData.approx_end_at ? 'status-cleared' : 'status-dispatched'" class="grid-value">
                  {{ incidentData.approx_end_at ? 'Cleared' : 'Dispatched' }}
                </span>
              </div>

              <!-- Incident Number with icon -->
              <div v-if="incidentData.incident_number" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">🔢</span>
                  Incident #
                </span>
                <span class="grid-value">{{ incidentData.incident_number }}</span>
              </div>

              <!-- Incident Type with icon -->
              <div v-if="incidentData.incident_type" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">{{ getEventTypeIcon(incidentData.incident_type) }}</span>
                  Type
                </span>
                <span class="grid-value">{{ incidentData.incident_type }}</span>
              </div>

              <!-- Address with icon -->
              <div v-if="incidentData.prime_street || incidentData.cross_streets" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">📍</span>
                  Address
                </span>
                <span class="grid-value">{{ formatLocationComprehensive(incidentData) }}</span>
              </div>

              <!-- Area with icon -->
              <div v-if="incidentData.area" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">🗺️</span>
                  Area
                </span>
                <span class="grid-value">{{ incidentData.area }}</span>
              </div>

              <!-- Alarm Level with icon -->
              <div v-if="incidentData.alarm_level !== undefined" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">🚨</span>
                  Alarm Level
                </span>
                <span class="grid-value alarm-level" :class="`alarm-${incidentData.alarm_level}`">
                  {{ incidentData.alarm_level }}
                </span>
              </div>

              <!-- Dispatch Time with icon -->
              <div v-if="incidentData.dispatch_time" class="grid-item">
                <span class="grid-label">
                  <span class="label-icon">⏰</span>
                  {{ getDispatchLabel() }}
                </span>
                <span class="grid-value">
                  {{ formatDateTime(incidentData.dispatch_time) }}<br>
                  <span class="duration-text">({{ dispatchDuration }})</span>
                </span>
              </div>
            </div>
          </div>

          <div v-else class="popover-no-data">
            No incident data available for this unit.
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { formatLocationComprehensive } from '../../utils/addressFormatting.js'

const props = defineProps({
  unitName: {
    type: String,
    required: true
  },
  incidentData: {
    type: Object,
    default: null
  },
  showOnHover: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['popover-opened'])

const isOpen = ref(false)
const triggerRef = ref(null)
const popoverRef = ref(null)
const currentTime = ref(new Date())
let timerInterval = null

const popoverStyle = computed(() => {
  if (!triggerRef.value || !isOpen.value) return {}

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  // Popover dimensions - responsive based on viewport
  const margin = 16
  const availableWidth = viewportWidth - (margin * 2)
  const availableHeight = viewportHeight - (margin * 2)

  // Use responsive dimensions, but maintain minimum sizes
  const popoverWidth = Math.min(400, Math.max(300, availableWidth))
  const popoverHeight = Math.min(300, Math.max(200, availableHeight))

  // Calculate initial position (prefer below and aligned to left of trigger)
  let top = triggerRect.bottom + 8
  let left = triggerRect.left

  // Check if popover would go off the right edge
  if (left + popoverWidth + margin > viewportWidth) {
    // Try to align to the right edge of the trigger
    left = triggerRect.right - popoverWidth
    // If still off screen, position at right edge with margin
    if (left < margin) {
      left = viewportWidth - popoverWidth - margin
    }
  }

  // Check if popover would go off the left edge
  if (left < margin) {
    left = margin
  }

  // Check if popover would go off the bottom edge
  if (top + popoverHeight + margin > viewportHeight) {
    // Try to position above the trigger
    top = triggerRect.top - popoverHeight - 8
    // If still off screen, position at bottom edge with margin
    if (top < margin) {
      top = viewportHeight - popoverHeight - margin
    }
  }

  // Final check: ensure popover is within viewport bounds
  top = Math.max(margin, Math.min(top, viewportHeight - popoverHeight - margin))
  left = Math.max(margin, Math.min(left, viewportWidth - popoverWidth - margin))

  return {
    top: `${top}px`,
    left: `${left}px`
  }
})

const togglePopover = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    emit('popover-opened', props.unitName, props.incidentData)
  }
}

const closePopover = () => {
  isOpen.value = false
}

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A'

  try {
    const date = new Date(dateTimeString)
    return date.toLocaleString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  } catch (error) {
    return dateTimeString
  }
}


const getEventTypeIcon = (eventType) => {
  if (!eventType) return '📋'

  const event_type = eventType.toLowerCase()
  if (event_type.includes('fire')) {
    return '🔥'
  } else if (event_type.includes('alarm')) {
    return '🚨'
  } else if (event_type.includes('vehicle')) {
    return '🚗'
  } else if (event_type.includes('medical')) {
    return '⚕️'
  } else if (event_type.includes('wire')) {
    return '⚡'
  } else {
    return '📋'
  }
}


const getDispatchLabel = () => {
  if (!props.incidentData) return 'Dispatched'
  return props.incidentData.approx_end_at ? 'Last Dispatched' : 'Dispatched'
}

const dispatchDuration = computed(() => {
  if (!props.incidentData?.dispatch_time) return 'N/A'

  try {
    const dispatchTime = new Date(props.incidentData.dispatch_time)
    // Use currentTime ref to make this reactive to timer updates
    const now = currentTime.value
    const diffMs = now - dispatchTime

    if (diffMs < 0) return 'Just now'

    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    // Format based on duration
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60
      if (remainingMinutes === 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''} ago`
      }
    } else {
      const remainingSeconds = diffSeconds % 60
      if (remainingSeconds === 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
      } else {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''} ago`
      }
    }
  } catch (error) {
    return 'N/A'
  }
})

// Close popover when clicking outside
const handleClickOutside = (event) => {
  if (isOpen.value &&
    triggerRef.value &&
    popoverRef.value &&
    !triggerRef.value.contains(event.target) &&
    !popoverRef.value.contains(event.target)) {
    closePopover()
  }
}

// Start timer when popover opens
const startTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval)
  }

  timerInterval = setInterval(() => {
    // Use Toronto timezone for current time
    currentTime.value = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Toronto" }))
  }, 1000) // Update every second
}

// Stop timer when popover closes
const stopTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

// Watch for popover open/close to manage timer
watch(isOpen, (newValue) => {
  if (newValue) {
    startTimer()
  } else {
    stopTimer()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  stopTimer()
})
</script>

<style scoped>
.popover-content {
  position: fixed;
  z-index: 9999;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 16px;
  max-width: 400px;
  min-width: 300px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.popover-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.popover-title {
  font-weight: 600;
  color: #111827;
  margin: 0;
  font-size: 16px;
}

.popover-close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.2s ease;
}

.popover-close-btn:hover {
  color: #6b7280;
  background-color: #f3f4f6;
}

.popover-data {
  margin-top: 4px;
}

.data-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 12px;
  align-items: start;
  font-size: 14px;
}

.grid-item {
  display: contents;
}

.grid-label {
  font-weight: 500;
  color: #374151;
  text-align: left;
  padding-right: 8px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.label-icon {
  font-size: 14px;
  opacity: 0.8;
}

.grid-value {
  color: #111827;
  word-break: break-word;
}

.duration-text {
  color: #6b7280;
  font-size: 13px;
  font-style: italic;
}

.status-cleared {
  color: #059669;
  font-weight: 500;
}

.status-dispatched {
  color: #dc2626;
  font-weight: 500;
}

.popover-no-data {
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

/* Alarm level styling */
.alarm-level {
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
}

.alarm-0,
.alarm-1 {
  background-color: #f3f4f6;
  color: #374151;
}

.alarm-2,
.alarm-3 {
  background-color: #fef3c7;
  color: #92400e;
}

.alarm-4 {
  background-color: #fecaca;
  color: #991b1b;
}
</style>
